import React, { useEffect, useRef } from "react"
import SEO from "../components/seo"
import Layout from "../components/layout"
import ContentBlock from "../components/ContentBlock/ContentBlock"
import PageTitle from "../components/PageTitle/PageTitle"
import { Container, Row, Col } from "react-bootstrap"
import { graphql } from "gatsby"
import { BLOCKS, INLINES } from "@contentful/rich-text-types"
import { documentToReactComponents } from "@contentful/rich-text-react-renderer"
import PostLinks from "../components/PostLinks"
import CrestPlaceholderImg from "../components/NewsBlock/CrestPlaceHolderImg"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faAngleRight } from "@fortawesome/pro-solid-svg-icons"
import Hls from "hls.js"

// HLS Video Player Component
const HlsVideoPlayer = ({ videoUrl, title }) => {
  const videoRef = useRef(null)

  useEffect(() => {
    if (!videoRef.current) return

    const video = videoRef.current

    // Check if HLS is supported
    if (Hls.isSupported()) {
      const hls = new Hls()
      hls.loadSource(videoUrl)
      hls.attachMedia(video)
      
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        // Video is ready to play
      })

      hls.on(Hls.Events.ERROR, (event, data) => {
        console.error('HLS error:', data)
      })

      return () => {
        hls.destroy()
      }
    } 
    // For Safari, which supports HLS natively
    else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = videoUrl
    }
  }, [videoUrl])

  return (
    <div style={{ margin: "20px 0" }}>
      <video 
        ref={videoRef}
        controls 
        style={{ width: "100%", maxWidth: "800px" }}
        aria-label={title}
      />
    </div>
  )
}

const PageTemplate = ({ data, pageContext }) => {
  console.log(data)
  const post = data.contentfulNewsPost
  //const embed = data.iframeEmbed
  const previous = pageContext.prev
  const next = pageContext.next
  //const { previous, next } = this.props.pageContext
  //console.log(post.childContentfulNewsPostNewsContentRichTextNode.json)
  //console.log(embed)

  const options = {
    renderNode: {
      [BLOCKS.EMBEDDED_ASSET]: ({
        data: {
          target: { fields },
        },
      }) => (
        <div
          dangerouslySetInnerHTML={{
            __html: `<img src="${fields.file["en-US"].url}" alt="${fields.title["en-US"]}" class="fluid" style="width: 100%; margin: 20px 0 20px 0" />`,
          }}
        />
      ),

      [BLOCKS.EMBEDDED_ENTRY]: node => {
        const fields = node.data.target.fields.imageLink
        const imgLnk = node.data.target.fields.url
        console.log(fields, imgLnk)
        return (
          <div
            dangerouslySetInnerHTML={{
              __html: `<a href="${imgLnk["en-US"]}"><img src="${fields["en-US"].fields.file["en-US"].url}" alt="${fields.title}" style="width: 100%" /></a>`,
            }}
          />
        )
      },

      // Embed iframe content
      [INLINES.EMBEDDED_ENTRY]: node => {
        const fields = node.data.target.fields
        const contentType = node.data.target.sys?.contentType?.sys?.id
        
        // Handle s3HostedVideoEmbed
        if (contentType === 's3HostedVideoEmbed' && fields?.link?.["en-US"]) {
          const videoUrl = fields.link["en-US"]
          const title = fields.title?.["en-US"] || "Video"
          
          return <HlsVideoPlayer videoUrl={videoUrl} title={title} />
        }
        
        // Handle other embedded entries with content or markdownContent
        const embed = fields?.content || fields?.markdownContent
        if (embed && embed["en-US"]) {
          console.log("embed data: " + embed["en-US"])
          return <div dangerouslySetInnerHTML={{ __html: `${embed["en-US"]}` }} />
        }
        
        // Fallback for unrecognized embedded entries
        console.warn("Unrecognized embedded entry type:", contentType, fields)
        return null
      },
    },
  }

  return (
    <Layout>
      <SEO title={post.title} />
      <Container fluid className="container-fluid-full">
        <PageTitle pageTitle={post.title}>{/* {page.pageTitle} */}</PageTitle>
        <ContentBlock>
          <Row>
            <Col sm={9}>
              <div>
                <p>
                  <strong>{post.createdAt}</strong>
                </p>
                <div>
                  {post.linkToOriginalArticle && (
                    <strong>
                      <a
                        href={post.linkToOriginalArticle}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ fontSize: "1.5rem" }}
                      >
                        Read original article{" "}
                        <FontAwesomeIcon
                          icon={faAngleRight}
                          style={{
                            marginRight: "5px",
                          }}
                          aria-hidden="true"
                        />
                      </a>
                    </strong>
                  )}
                </div>
                {documentToReactComponents(
                  post.childContentfulNewsPostPostContentRichTextNode.json,
                  options
                )}
              </div>
              {/* <div dangerouslySetInnerHTML={{__html: embed.edges[0].node.childMarkdownRemark.html}}></div> */}
            </Col>
            {!post.image ? (
              <Col sm={3}>
                <CrestPlaceholderImg />
              </Col>
            ) : (
              <Col sm={3}>
                <a
                  href={post.linkToOriginalArticle}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    style={{
                      width: "100%",
                    }}
                    src={post.image ? post.image.fluid.src : null}
                    alt={post.title}
                    className="rounded"
                  />
                </a>
                <p className="featuredImageCaption">{post.image.description}</p>
              </Col>
            )}

            {/* <div>

            <img src={post.image ? post.image.fluid.src : null} 
                style={{ width: '100%', marginBottom: '20px'}}
                className="rounded mx-auto d-block"    
            />
            <p className="featuredImageCaption">{post.image.description}</p>
            </div>
                </Col> */}
          </Row>

          <PostLinks previous={previous} next={next} />
        </ContentBlock>
      </Container>
    </Layout>
  )
}

export const getBlogPost = graphql`
  query ContentfulBlogPostBySlug($slug: String) {
    contentfulNewsPost(slug: { eq: $slug }) {
      title
      slug
      updatedAt(formatString: "dddd MMMM Do, YYYY")
      childContentfulNewsPostPostExcerptTextNode {
        postExcerpt
      }
      childContentfulNewsPostPostContentRichTextNode {
        json
      }
      image: featuredImage {
        file {
          url
        }
        title
        description
        fluid(maxWidth: 800) {
          sizes
          src
          srcSet
        }
      }
      linkToOriginalArticle
      createdAt(formatString: "ddd MMM Do, YYYY")
    }
  }
`

export default PageTemplate
